import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Login.css";
import logo from "../../../Assets/LOGO-GENYDMS(2).png";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "https://localhost:8000";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenValid, setTokenValid] = useState(true); // assume valid until proven otherwise
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const triggerPopup = (msg) => {
    setError(msg);
    setShowPopup(true);
  };

  // If no token/email in URL, mark as invalid immediately
  useEffect(() => {
    if (!token || !email) {
      setTokenValid(false);
    }
  }, [token, email]);

  const handleReset = async () => {
    if (!newPassword || !confirmPassword) {
      triggerPopup("Both password fields are required.");
      return;
    }
    if (newPassword.length < 8) {
      triggerPopup("Password must be at least 8 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      triggerPopup("Passwords do not match. Please try again.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/webmanager/forgot-password/reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          token,
          email,
          password: newPassword,
          password_confirmation: confirmPassword,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setDone(true);
        setTimeout(() => navigate("/login"), 3000);
      } else if (res.status === 422 || res.status === 400) {
        // Token expired or invalid
        setTokenValid(false);
      } else {
        triggerPopup(data.error || "Failed to reset password. Please try again.");
      }
    } catch {
      triggerPopup("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-0 login-container">
      <div className="row g-0 min-vh-100">

        {/* LEFT PANEL */}
        <div className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white left-panel">
          <h1 className="welcome-heading">Reset Your Password</h1>
          <div />
          <h1 className="welcome-heading">
            Choose a <span>new secure password</span> for your account.
          </h1>
          <div>
            <p>
              Make sure your new password is strong and not used
              <br /> on any other platform.
            </p>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-md-6 d-flex justify-content-center align-items-center right-panel">
          <div className="card shadow border-0" style={{ width: "360px" }}>

            <div
              className="card-header text-center text-white"
              style={{ backgroundColor: "#001F54" }}
            >
              Set New Password
            </div>

            <div className="card-body px-3 py-3">

              {/* LOGO */}
              <div className="logo-wrapper mx-auto mb-3">
                <img src={logo} alt="Logo" className="logo-img" />
              </div>

              {/* ── INVALID / EXPIRED TOKEN ── */}
              {!tokenValid && (
                <div className="text-center py-2">
                  <div style={{ fontSize: 48 }}>⛔</div>
                  <h6 className="mt-3 fw-bold text-danger">
                    Link Invalid or Expired
                  </h6>
                  <p className="text-muted small mb-3">
                    This password reset link is no longer valid. Reset links
                    expire after <strong>60 minutes</strong>.
                  </p>
                  <button
                    type="button"
                    className="btn btn-sm w-100 text-white mb-2"
                    style={{ backgroundColor: "#001F54" }}
                    onClick={() => navigate("/forgot-password")}
                  >
                    Request New Link
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm w-100 btn-outline-secondary"
                    onClick={() => navigate("/login")}
                  >
                    Back to Login
                  </button>
                </div>
              )}

              {/* ── RESET FORM ── */}
              {tokenValid && !done && (
                <>
                  <p className="text-muted small mb-3">
                    Resetting password for <strong>{email}</strong>
                  </p>

                  <label className="form-label fw-bold mb-1">NEW PASSWORD:</label>
                  <div className="d-flex gap-2 mb-3">
                    <input
                      type={showNew ? "text" : "password"}
                      className="form-control form-control-sm"
                      placeholder="Minimum 8 characters"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value.replace(/\s/g, ""))}
                      disabled={loading}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowNew(!showNew)}
                      disabled={loading}
                    >
                      {showNew ? "Hide" : "Show"}
                    </button>
                  </div>

                  <label className="form-label fw-bold mb-1">CONFIRM PASSWORD:</label>
                  <div className="d-flex gap-2 mb-3">
                    <input
                      type={showConfirm ? "text" : "password"}
                      className="form-control form-control-sm"
                      placeholder="Re-enter new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value.replace(/\s/g, ""))}
                      onKeyDown={(e) => e.key === "Enter" && !loading && handleReset()}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowConfirm(!showConfirm)}
                      disabled={loading}
                    >
                      {showConfirm ? "Hide" : "Show"}
                    </button>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm text-white w-100 d-flex align-items-center justify-content-center gap-2"
                    style={{ backgroundColor: "#4CAF50" }}
                    onClick={handleReset}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        Resetting...
                      </>
                    ) : (
                      "Reset Password"
                    )}
                  </button>
                </>
              )}

              {/* ── SUCCESS ── */}
              {done && (
                <div className="text-center py-2">
                  <div style={{ fontSize: 48 }}></div>
                  <h6 className="mt-3 fw-bold" style={{ color: "#4CAF50" }}>
                    Password Reset Successful!
                  </h6>
                  <p className="text-muted small">
                    Your password has been updated. Redirecting to login...
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer text-white py-3">
        <div className="ps-5">
          © 2026 <span className="fw-bold">OneFinCore</span> All Rights Reserved.
        </div>
      </footer>

      {/* ERROR POPUP */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h5 className="text-danger text-center">⚠ Error</h5>
            <p className="text-center">{error}</p>
            <button
              className="btn btn-danger w-100"
              type="button"
              onClick={() => {
                setShowPopup(false);
                setError("");
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResetPassword;